import { useCallback, useEffect, useRef, useState } from 'react';
import {
  emptySupportChat,
  readSupportChat,
  removeSupportChat,
  saveSupportChat,
  SUPPORT_CHAT_TTL,
  SupportChat,
} from 'utils/supportChatStorage';
import { useSupportAssistant } from './useSupportAssistant';

// The owning component is keyed by user and tenant, isolating requests and state.
export function useSupportChatSession(storageKey: string) {
  const assistant = useSupportAssistant();
  const [chat, setChat] = useState(() => readSupportChat(storageKey));
  const current = useRef(chat);
  const generation = useRef(0);
  const pending = useRef(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedMessage, setFailedMessage] = useState<string | null>(null);
  const commit = useCallback(
    (next: SupportChat) => {
      current.current = next;
      setChat(next);
      saveSupportChat(storageKey, next);
    },
    [storageKey]
  );
  const reset = useCallback(() => {
    generation.current++;
    pending.current = false;
    setSending(false);
    setError(null);
    setFailedMessage(null);
    commit(emptySupportChat());
    removeSupportChat(storageKey);
  }, [commit, storageKey]);
  const expire = useCallback(() => {
    if (current.current.expiresAt && current.current.expiresAt <= Date.now()) {
      reset();
      return true;
    }
    return false;
  }, [reset]);
  useEffect(
    () => () => {
      generation.current++;
    },
    []
  );
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') expire();
    };
    window.addEventListener('focus', expire);
    document.addEventListener('visibilitychange', onVisible);
    const timer = chat.expiresAt
      ? window.setTimeout(expire, Math.max(0, chat.expiresAt - Date.now()))
      : undefined;
    return () => {
      window.removeEventListener('focus', expire);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearTimeout(timer);
    };
  }, [chat.expiresAt, expire]);
  const setDraft = (draft: string) => {
    expire();
    commit({
      ...current.current,
      draft: draft.slice(0, 2000),
      expiresAt: current.current.expiresAt || Date.now() + SUPPORT_CHAT_TTL,
    });
  };
  const send = async (retry = false) => {
    if (expire()) return;
    if (pending.current || !assistant.canChat) return;
    const message = (retry ? failedMessage : current.current.draft)?.trim();
    if (!message || message.length > 2000) return;
    pending.current = true;
    setSending(true);
    setError(null);
    setFailedMessage(null);
    const requestGeneration = generation.current;
    const before = current.current;
    commit({
      ...before,
      draft: retry ? before.draft : '',
      expiresAt: Date.now() + SUPPORT_CHAT_TTL,
      messages: retry ? before.messages : [...before.messages, { role: 'user', text: message }],
    });
    try {
      const response = await assistant.ask(message, before.conversationId);
      if (generation.current !== requestGeneration || expire()) return;
      const result = response.data?.askSupportAssistant;
      if (!result?.success || !result.answer)
        throw new Error(result?.error || 'Unable to get a response. Please try again.');
      commit({
        ...current.current,
        conversationId: result.conversationId || before.conversationId,
        messages: [
          ...current.current.messages,
          { role: 'assistant', text: result.answer, citations: result.citations ?? [] },
        ],
      });
    } catch {
      if (generation.current !== requestGeneration || expire()) return;
      setError(
        'We couldn’t get a response. Try again, or start a new chat if this conversation is no longer available.'
      );
      setFailedMessage(message);
    } finally {
      if (generation.current === requestGeneration) {
        pending.current = false;
        setSending(false);
      }
    }
  };
  return { ...assistant, chat, setDraft, reset, send, sending, error, failedMessage };
}
